import { useState, useEffect, useCallback } from 'react';
import { ParkingSquare, Plus, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@mustauto/ui/web';
import { useToast } from '@mustauto/ui/web';
import { SOFT_BADGE_CLASSES, SOFT_SURFACE_CLASSES, SOFT_TEXT_CLASSES } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import { DEFAULT_SLOT_KIND, SLOT_KINDS, SLOT_KIND_LABELS, type SlotKind } from '@mustauto/types';
import { useCan } from '@/hooks/useCan';

interface Slot {
  id: string;
  label: string;
  /** Opcional: API anterior à F3.5 não manda — e toda vaga de lá é `bay`. */
  kind?: SlotKind;
  active: boolean;
  createdAt: string;
}

interface SlotStatus {
  slotId: string;
  label: string;
  osId: string | null;
  plate: string | null;
  status: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  in_analysis: 'Em Análise',
  in_execution: 'Em Execução',
  waiting_part: 'Ag. Peça',
  outsourced: 'Terceirizado',
  waiting_payment: 'Ag. Pagamento',
};

const STATUS_COLORS: Record<string, string> = {
  in_analysis: SOFT_BADGE_CLASSES.blue,
  in_execution: SOFT_BADGE_CLASSES.orange,
  waiting_part: SOFT_BADGE_CLASSES.yellow,
  outsourced: SOFT_BADGE_CLASSES.indigo,
  waiting_payment: SOFT_BADGE_CLASSES.pink,
};

export function WorkshopSlotsPage() {
  const { toast } = useToast();
  const { isDemo } = useAuth();
  // O mapa é de todos; cadastrar e mexer nas vagas é `oficina.configurar` (Fase 3).
  const canConfigure = useCan()('oficina.configurar');
  const readOnly = isDemo || !canConfigure;
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotStatus, setSlotStatus] = useState<SlotStatus[]>([]);
  const [loading, setLoading] = useState(true);
  /** Busca falhou — diferente de "a oficina não cadastrou vaga nenhuma". */
  const [loadError, setLoadError] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newKind, setNewKind] = useState<SlotKind>(DEFAULT_SLOT_KIND);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [slotsRes, statusRes] = await Promise.all([
        api.get('/workshops/my/slots'),
        api.get('/workshops/my/slots/status'),
      ]);
      setSlots(slotsRes.data.slots ?? []);
      setSlotStatus(statusRes.data.slots ?? []);
      setLoadError(false);
    } catch {
      // Sem isto a tela oferecia "Cadastrar primeira vaga" a quem já tem dez —
      // e o gestor cadastrava tudo de novo.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    try {
      await api.post('/workshops/my/slots', { label: newLabel.trim(), kind: newKind });
      setNewLabel('');
      setNewKind(DEFAULT_SLOT_KIND);
      loadData();
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao criar vaga',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  const handleUpdate = async (slotId: string) => {
    if (!editLabel.trim()) return;
    try {
      await api.patch(`/workshops/my/slots/${slotId}`, { label: editLabel.trim() });
      setEditingId(null);
      loadData();
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao renomear vaga',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  // Vaga ↔ cabine de pintura (F3.5). A API recusa (409) virar cabine com um
  // carro em cima, e explica qual OS está lá.
  const handleKind = async (slotId: string, kind: SlotKind) => {
    try {
      await api.patch(`/workshops/my/slots/${slotId}`, { kind });
      loadData();
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível mudar o tipo',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  const handleToggle = async (slotId: string, active: boolean) => {
    try {
      await api.patch(`/workshops/my/slots/${slotId}`, { active: !active });
      loadData();
    } catch (err) {
      toast({
        type: 'error',
        title: active ? 'Falha ao desativar vaga' : 'Falha ao ativar vaga',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  const handleDelete = async (slotId: string) => {
    try {
      await api.delete(`/workshops/my/slots/${slotId}`);
      loadData();
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao excluir vaga',
        description: formatApiError(err, 'Verifique se a vaga não está ocupada e tente novamente.'),
      });
    }
  };

  const getStatusForSlot = (slotId: string) =>
    slotStatus.find((s) => s.slotId === slotId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Mapa de Vagas</h1>
        <p className="text-sm text-muted-foreground">
          Gerencie as vagas da oficina e veja quais estão ocupadas
        </p>
      </div>

      {/* Slot status grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {slotStatus.map((slot) => {
          const isOccupied = !!slot.osId;
          return (
            <Card
              key={slot.slotId}
              className={isOccupied ? SOFT_SURFACE_CLASSES.red : SOFT_SURFACE_CLASSES.green}
            >
              <CardContent className="pt-4 text-center">
                <div className="text-2xl mb-1">
                  {isOccupied ? '🔴' : '🟢'}
                </div>
                <p className="font-semibold">{slot.label}</p>
                {isOccupied ? (
                  <>
                    <p className="text-sm font-mono font-bold mt-1">
                      {slot.plate}
                    </p>
                    <Badge
                      className={`mt-1 ${STATUS_COLORS[slot.status ?? ''] ?? SOFT_BADGE_CLASSES.gray}`}
                    >
                      {STATUS_LABELS[slot.status ?? ''] ?? slot.status}
                    </Badge>
                  </>
                ) : (
                  <p className={`text-sm mt-1 ${SOFT_TEXT_CLASSES.green}`}>Livre</p>
                )}
              </CardContent>
            </Card>
          );
        })}

        {loadError && slotStatus.length === 0 && (
          <div className="col-span-full">
            <ErrorState
              title="Não foi possível carregar as vagas"
              onRetry={() => { setLoading(true); loadData(); }}
            />
          </div>
        )}

        {!loadError && slotStatus.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={<ParkingSquare size={32} />}
              title="Nenhuma vaga cadastrada"
              description="Cadastre as vagas/elevadores da sua oficina pra associar a cada OS em execução."
              action={readOnly ? undefined : {
                label: 'Cadastrar primeira vaga',
                onClick: () => {
                  const input = document.querySelector<HTMLInputElement>('input[placeholder*="Nome da vaga"]');
                  input?.focus();
                  input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                },
              }}
            />
          </div>
        )}
      </div>

      {/* Management section */}
      <Card>
        <CardHeader>
          <CardTitle>Gerenciar Vagas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sem isto o gestor via a lista de gerenciamento vazia com o campo
              "Adicionar" logo acima e recadastrava as vagas — e a API não tem
              unicidade em `label`, então o duplicado entra calado. O grid de
              status acima já avisa da falha; aqui basta não convidar ao erro. */}
          {loadError && slots.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              A lista de vagas não pôde ser carregada. Recarregue antes de cadastrar,
              para não duplicar vagas que já existem.
            </p>
          ) : (
          <>
          {/* Add new */}
          {!readOnly && (
          <div className="flex gap-2">
            <label htmlFor="new-slot-kind" className="sr-only">Tipo</label>
            <select
              id="new-slot-kind"
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as SlotKind)}
              className="h-10 rounded-md border border-input bg-background px-2 text-sm"
            >
              {SLOT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {SLOT_KIND_LABELS[k]}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={newLabel}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewLabel(e.target.value)}
              placeholder={newKind === 'paint_booth' ? 'Nome da cabine (ex: Cabine 1)' : 'Nome da vaga (ex: Vaga 1, Elevador A)'}
              maxLength={50}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleCreate()}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              onClick={handleCreate}
              disabled={!newLabel.trim()}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              <Plus size={16} className="mr-1" />
              Adicionar
            </button>
          </div>
          )}

          {newKind === 'paint_booth' && !readOnly && (
            <p className="text-xs text-muted-foreground">
              A cabine não entra na escolha de vaga da OS nem na contagem de vagas livres: o carro passa
              por ela na etapa &quot;Cabine de pintura&quot;. Quem está nela aparece no Dashboard.
            </p>
          )}

          {/* List */}
          <div className="divide-y">
            {slots.map((slot) => {
              const kind = slot.kind ?? DEFAULT_SLOT_KIND;
              const isBooth = kind === 'paint_booth';
              const status = getStatusForSlot(slot.id);
              const isOccupied = !!status?.osId;

              return (
                <div
                  key={slot.id}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex items-center gap-3">
                    <span className={slot.active ? '' : 'opacity-40'}>
                      {/* A cabine não tem "ocupada" aqui: a ocupação dela é a
                          etapa, e está no Dashboard. */}
                      {isBooth ? '🎨' : isOccupied ? '🔴' : '🟢'}
                    </span>
                    {editingId === slot.id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditLabel(e.target.value)}
                          className="h-8 w-48 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                            e.key === 'Enter' && handleUpdate(slot.id)
                          }
                        />
                        <button
                          className="inline-flex items-center rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                          onClick={() => handleUpdate(slot.id)}
                        >
                          Salvar
                        </button>
                        <button
                          className="inline-flex items-center rounded-md px-3 py-1 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setEditingId(null)}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <span
                        className={`font-medium ${!slot.active ? 'line-through text-muted-foreground' : ''}`}
                      >
                        {slot.label}
                      </span>
                    )}
                    {isBooth && <Badge className={SOFT_BADGE_CLASSES.violet}>{SLOT_KIND_LABELS.paint_booth}</Badge>}
                    {!slot.active && (
                      <Badge variant="secondary">Inativa</Badge>
                    )}
                  </div>
                  {!readOnly && <div className="flex items-center gap-2">
                    {!readOnly && (
                      <select
                        aria-label={`Tipo de ${slot.label}`}
                        value={kind}
                        onChange={(e) => void handleKind(slot.id, e.target.value as SlotKind)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                      >
                        {SLOT_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {SLOT_KIND_LABELS[k]}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      aria-label="Renomear vaga"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
                      onClick={() => {
                        setEditingId(slot.id);
                        setEditLabel(slot.label);
                      }}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      aria-label={slot.active ? 'Desativar vaga' : 'Ativar vaga'}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
                      onClick={() => handleToggle(slot.id, slot.active)}
                    >
                      {slot.active ? '🟢' : '🔴'}
                    </button>
                    <button
                      aria-label="Excluir vaga"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-500 hover:bg-accent hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                      onClick={() => handleDelete(slot.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>}
                </div>
              );
            })}
          </div>
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
